import React, { useState, useEffect } from 'react';
import './CreativeBriefForm.css';
import ProgressLoader from './ProgressLoader';

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Canada', 'Australia',
  'Singapore', 'UAE', 'Germany', 'France', 'Brazil', 'Other'
];

const INDUSTRIES = [
  'E-commerce / Online Shopping',
  'Fashion & Apparel',
  'Footwear',
  'Beauty & Personal Care',
  'Health & Wellness',
  'Fitness / Sports & Outdoors',
  'Electronics & Gadgets',
  'Mobile Apps / SaaS',
  'Fintech / Banking / Insurance',
  'Edtech / Online Learning',
  'Food Delivery / Restaurants',
  'Travel & Hospitality',
  'Real Estate',
  'Automobiles / Mobility',
  'Home & Living / Furniture',
  'Grocery / Quick Commerce',
  'Gaming',
  'Entertainment / OTT / Media',
  'Telecom / Internet Services',
  'B2B / Enterprise Solutions',
  'Others'
];

const BRAND_PERSONALITIES = [
  'Premium',
  'Mass-market / Value for Money',
  'Fun & Playful',
  'Bold & Edgy',
  'Minimal & Modern',
  'Traditional / Heritage',
  'Trustworthy / Reliable',
  'Youthful',
  'Family-friendly',
  'Innovative / Tech-forward',
  'Aspirational / Luxurious',
  'Professional / Serious',
  'Quirky / Offbeat',
  'Warm & Caring',
  'Eco-conscious / Sustainable'
];

const CAMPAIGN_GOALS = [
  'Brand Awareness / Reach',
  'Video Views',
  'Traffic (Website / Landing Page Visits)',
  'App Installs',
  'Lead Generation (Form Fills)',
  'Add to Cart / Product Page Views',
  'Purchases / Sales / Conversions',
  'Repeat Purchases / Retention',
  'Store Visits / Offline Footfall',
  'Engagement (Likes, Comments, Shares)',
  'Other'
];

const AGE_GROUPS = ['13-17', '18-24', '25-34', '35-44', '45-54', '55+'];

const GENDERS = ['All', 'Male', 'Female', 'Other / Non-binary', 'Custom'];

const AUDIENCE_TYPES = [
  'New Users / Prospecting',
  'Retargeting – Website Visitors',
  'Retargeting – App Users',
  'Retargeting – Add to Cart, No Purchase',
  'Retargeting – Past Purchasers',
  'High-Value / Loyal Customers',
  'Lookalike of Converters / Purchasers',
  'Lookalike of Website Visitors',
  'CRM List / Custom List',
  'Interest-based Audience',
  'Contextual / Content-based Audience',
  'Other'
];

const OFFER_TYPES = [
  'No Offer / Always-On Campaign',
  'Flat Discount (%)',
  'Flat Discount (Value)',
  'Tiered Discount',
  'Cashback',
  'Free Shipping',
  'Buy X Get Y',
  'Free Trial / Demo',
  'Introductory Offer / Launch Offer',
  'Seasonal Sale',
  'EMI / No-Cost EMI / Financing',
  'Loyalty / Referral Rewards',
  'Other'
];

const CREATIVE_OBJECTIVES = [
  'Drive Clicks to Website / App Store',
  'Drive App Installs',
  'Drive Product Page Visits',
  'Drive Purchases / Transactions',
  'Drive Form Fills / Leads',
  'Drive Store Visits',
  'Drive Video Views / Completion',
  'Drive Engagement',
  'Drive Add to Cart / Wishlist',
  'Educate about New Feature / Launch',
  'Promote Limited-time Offer / Sale',
  'Other'
];

const CREATIVE_FORMATS = [
  { value: '1080x1080', label: '1:1 (Square Feed/Carousel)' },
  { value: '1080x1350', label: '4:5 (Vertical Feed)' },
  { value: '1080x1920', label: '9:16 (Stories/Reels)' },
  { value: '1920x1080', label: '1.91:1 (Landscape Feed)' }
];

function CreativeBriefForm() {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Step 1: Mandatory fields
  const [brandName, setBrandName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [referenceAds, setReferenceAds] = useState([]);
  const [referenceAdUrls, setReferenceAdUrls] = useState('');
  const [country, setCountry] = useState('');

  // Step 2: Auto-filled & editable fields
  const [industryCategory, setIndustryCategory] = useState('');
  const [brandDescription, setBrandDescription] = useState('');
  const [brandPersonality, setBrandPersonality] = useState([]);
  const [keyCompetitors, setKeyCompetitors] = useState('');
  const [primaryCampaignGoal, setPrimaryCampaignGoal] = useState('');
  const [ageGroups, setAgeGroups] = useState([]);
  const [gender, setGender] = useState('All');
  const [targetLocations, setTargetLocations] = useState('');
  const [languages, setLanguages] = useState('');
  const [audienceType, setAudienceType] = useState([]);
  const [offerType, setOfferType] = useState('');
  const [offerDetails, setOfferDetails] = useState('');
  const [creativeObjective, setCreativeObjective] = useState('');
  const [creativeFormats, setCreativeFormats] = useState(['1080x1080']);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [numberOfVariants, setNumberOfVariants] = useState(2);

  const [creatives, setCreatives] = useState([]);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [targetProgress, setTargetProgress] = useState(0);

  // Smooth progress animation
  useEffect(() => {
    if (progress < targetProgress) {
      const increment = Math.max(1, (targetProgress - progress) / 20);
      const timer = setTimeout(() => {
        setProgress(prev => Math.min(prev + increment, targetProgress));
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [progress, targetProgress]);

  // Load form data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('creativeBriefFormData');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      if (parsed.brandName) setBrandName(parsed.brandName);
      if (parsed.websiteUrl) setWebsiteUrl(parsed.websiteUrl);
      if (parsed.country) setCountry(parsed.country);
      if (parsed.industryCategory) setIndustryCategory(parsed.industryCategory);
      if (parsed.brandDescription) setBrandDescription(parsed.brandDescription);
      if (parsed.brandPersonality) setBrandPersonality(parsed.brandPersonality);
      if (parsed.keyCompetitors) setKeyCompetitors(parsed.keyCompetitors);
      if (parsed.primaryCampaignGoal) setPrimaryCampaignGoal(parsed.primaryCampaignGoal);
      if (parsed.ageGroups) setAgeGroups(parsed.ageGroups);
      if (parsed.gender) setGender(parsed.gender);
      if (parsed.targetLocations) setTargetLocations(parsed.targetLocations);
      if (parsed.languages) setLanguages(parsed.languages);
      if (parsed.audienceType) setAudienceType(parsed.audienceType);
      if (parsed.offerType) setOfferType(parsed.offerType);
      if (parsed.offerDetails) setOfferDetails(parsed.offerDetails);
      if (parsed.creativeObjective) setCreativeObjective(parsed.creativeObjective);
      if (parsed.creativeFormats) setCreativeFormats(parsed.creativeFormats);
      if (parsed.additionalNotes) setAdditionalNotes(parsed.additionalNotes);
      if (parsed.numberOfVariants) setNumberOfVariants(parsed.numberOfVariants);
    }
  }, []);

  // Save form data to localStorage whenever relevant fields change
  useEffect(() => {
    const formData = {
      brandName,
      websiteUrl,
      country,
      industryCategory,
      brandDescription,
      brandPersonality,
      keyCompetitors,
      primaryCampaignGoal,
      ageGroups,
      gender,
      targetLocations,
      languages,
      audienceType,
      offerType,
      offerDetails,
      creativeObjective,
      creativeFormats,
      additionalNotes,
      numberOfVariants
    };
    localStorage.setItem('creativeBriefFormData', JSON.stringify(formData));
  }, [brandName, websiteUrl, country, industryCategory, brandDescription, brandPersonality,
      keyCompetitors, primaryCampaignGoal, ageGroups, gender, targetLocations, languages,
      audienceType, offerType, offerDetails, creativeObjective, creativeFormats,
      additionalNotes, numberOfVariants]);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setReferenceAds(files);
  };

  const handleFetchDetails = async () => {
    setAnalyzing(true);
    setError(null);
    setProgress(0);
    setTargetProgress(0);
    setProgressMessage('Starting...');

    try {
      const formData = new FormData();
      formData.append('brandName', brandName);
      formData.append('websiteUrl', websiteUrl);
      formData.append('country', country);

      // Add uploaded files
      referenceAds.forEach((file, index) => {
        formData.append(`referenceAds`, file);
      });

      // Add reference URLs if provided
      if (referenceAdUrls.trim()) {
        formData.append('referenceAdUrls', referenceAdUrls);
      }

      const response = await fetch(`${API_URL}/analyze-brand-sse`, {
        method: 'POST',
        body: formData
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');

        // Keep the last incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));

              if (data.error) {
                throw new Error(data.error);
              }

              if (data.progress !== undefined) {
                setTargetProgress(data.progress);
                setProgressMessage(data.message || '');
              }

              if (data.complete && data.data) {
                // Set progress to 100% first
                setTargetProgress(100);
                setProgressMessage('Complete!');

                // Wait for animation to reach 100% before proceeding
                setTimeout(() => {
                  // Auto-fill fields from Gemini response
                  const analysis = data.data;
                  if (analysis.industryCategory) setIndustryCategory(analysis.industryCategory);
                  if (analysis.brandDescription) setBrandDescription(analysis.brandDescription);
                  if (analysis.brandPersonality) setBrandPersonality(analysis.brandPersonality);
                  if (analysis.keyCompetitors) setKeyCompetitors(analysis.keyCompetitors.join(', '));
                  if (analysis.primaryCampaignGoal) setPrimaryCampaignGoal(analysis.primaryCampaignGoal);
                  if (analysis.ageGroups) setAgeGroups(analysis.ageGroups);
                  if (analysis.gender) setGender(analysis.gender);
                  if (analysis.targetLocations) setTargetLocations(analysis.targetLocations.join(', '));
                  if (analysis.languages) setLanguages(analysis.languages.join(', '));
                  if (analysis.audienceType) setAudienceType(analysis.audienceType);
                  if (analysis.offerType) setOfferType(analysis.offerType);
                  if (analysis.creativeObjective) setCreativeObjective(analysis.creativeObjective);

                  setStep(2);
                }, 1000); // Wait 1 second for animation to complete
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', parseError);
            }
          }
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
      setProgress(0);
      setTargetProgress(0);
      setProgressMessage('');
    }
  };

  const handleGenerateCreatives = async () => {
    setLoading(true);
    setError(null);
    setProgress(0);
    setTargetProgress(0);
    setProgressMessage('Starting creative generation...');

    try {
      const payload = {
        brandName,
        websiteUrl,
        country,
        industryCategory,
        brandDescription,
        brandPersonality,
        keyCompetitors: keyCompetitors.split(',').map(c => c.trim()),
        primaryCampaignGoal,
        ageGroups,
        gender,
        targetLocations: targetLocations.split(',').map(l => l.trim()),
        languages: languages.split(',').map(l => l.trim()),
        audienceType,
        offerType,
        offerDetails,
        creativeObjective,
        creativeFormats,
        additionalNotes,
        numberOfVariants
      };

      const response = await fetch(`${API_URL}/generate-creatives-from-brief-sse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');

        // Keep the last incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));

              if (data.error) {
                throw new Error(data.error);
              }

              if (data.progress !== undefined) {
                setTargetProgress(data.progress);
                setProgressMessage(data.message || '');
              }

              if (data.complete && data.data) {
                // Set progress to 100% first
                setTargetProgress(100);
                setProgressMessage('Complete!');

                // Wait for animation to reach 100% before proceeding
                setTimeout(() => {
                  setCreatives(data.data.creatives || []);
                  setStep(3);
                }, 1000); // Wait 1 second for animation to complete
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', parseError);
            }
          }
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setProgress(0);
      setTargetProgress(0);
      setProgressMessage('');
    }
  };

  const handleDownload = async (imageUrl, filename) => {
    try {
      const response = await fetch(`${API_URL}${imageUrl}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download image');
    }
  };

  const toggleArrayValue = (array, setter, value) => {
    if (array.includes(value)) {
      setter(array.filter(v => v !== value));
    } else {
      setter([...array, value]);
    }
  };

  return (
    <div className="creative-brief-container">
      {(analyzing || loading) && progress > 0 && (
        <ProgressLoader progress={progress} message={progressMessage} />
      )}

      <div className="right-top-absolute">
        <img className="inline-flex 2xl:max-w-80 max-w-44" src="/upside_right.svg" alt="Ultra RightTop" />
      </div>

      <div className="left-bottom-absolute">
        <img className="inline-flex 2xl:max-w-72 max-w-40" src="/downside-left.svg" alt="Ultra RightTop" />
      </div>

      <header className="brief-header">
        <div className="header-title">
          <div className="title-group">
            <div className="main-title">
               <img src="/iris_logo.svg" alt="Ultra Iris Logo" className="ultra-iris-logo" />
            </div>
            <p className="header-subtitle">Professional Ad Creative Generator</p>
          </div>
        </div>
      </header>

      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
        </div>
      )}

      {/* Step 1: Mandatory Fields */}
      {step === 1 && (
        <div className="brief-form">
          <h2 className="form-section-title">Step 1: Brand Information</h2>

          <div className="form-field">
            <label>Brand Name <span className="required">*</span></label>
            <input
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="Enter your brand name"
              required
            />
          </div>

          <div className="form-field">
            <label>Website / App Link <span className="required">*</span></label>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://www.yourbrand.com"
              required
            />
          </div>

          <div className="form-field">
            <label>Reference Ads (Upload Files)</label>
            <input
              type="file"
              onChange={handleFileUpload}
              multiple
              accept="image/*"
            />
            {referenceAds.length > 0 && (
              <small className="file-count">{referenceAds.length} file(s) selected</small>
            )}
          </div>

          <div className="form-field">
            <label>Reference Ads (URLs)</label>
            <textarea
              value={referenceAdUrls}
              onChange={(e) => setReferenceAdUrls(e.target.value)}
              placeholder="Paste image URLs here, one per line&#10;https://example.com/ad1.jpg&#10;https://example.com/ad2.png"
              rows="3"
            />
            <small className="field-hint">Provide either uploaded files or URLs (or both)</small>
          </div>

          <div className="form-field">
            <label>Country <span className="required">*</span></label>
            <select value={country} onChange={(e) => setCountry(e.target.value)} required>
              <option value="">Select country</option>
              {COUNTRIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <button
            className="primary-button"
            onClick={handleFetchDetails}
            disabled={!brandName || !websiteUrl || (referenceAds.length === 0 && !referenceAdUrls.trim()) || !country || analyzing}
          >
            {analyzing ? 'Analyzing Brand...' : 'Fetch Details →'}
          </button>
        </div>
      )}

      {/* Step 2: Auto-filled & Editable Fields */}
      {step === 2 && (
        <div className="brief-form">
          <h2 className="form-section-title">Step 2: Campaign Details</h2>
          <p className="form-subtitle">Review and edit the auto-filled information below</p>

          {/* Brand Information Section */}
          <div className="form-section">
            <h3 className="section-header">📋 Brand Information</h3>

            <div className="form-field form-width-short">
              <label>Industry / Category</label>
              <select value={industryCategory} onChange={(e) => setIndustryCategory(e.target.value)}>
                <option value="">Select industry</option>
                {INDUSTRIES.map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Brand Description</label>
              <textarea
                value={brandDescription}
                onChange={(e) => setBrandDescription(e.target.value)}
                placeholder="Describe your brand, offering, and value proposition..."
                rows="3"
              />
            </div>

            <div className="form-field">
              <label>Brand Personality</label>
              <div className="checkbox-grid">
                {BRAND_PERSONALITIES.map(personality => (
                  <label key={personality} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={brandPersonality.includes(personality)}
                      onChange={() => toggleArrayValue(brandPersonality, setBrandPersonality, personality)}
                    />
                    {personality}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-field">
              <label>Key Competitors <span className="required">*</span></label>
              <input
                type="text"
                value={keyCompetitors}
                onChange={(e) => setKeyCompetitors(e.target.value)}
                placeholder="Competitor 1, Competitor 2, Competitor 3"
                required
              />
              <small className="field-hint">Separate multiple competitors with commas</small>
            </div>
          </div>

          {/* Campaign Goals Section */}
          <div className="form-section">
            <h3 className="section-header">🎯 Campaign Goals</h3>

            <div className="form-field form-width-short">
              <label>Primary Campaign Goal <span className="required">*</span></label>
              <select value={primaryCampaignGoal} onChange={(e) => setPrimaryCampaignGoal(e.target.value)} required>
                <option value="">Select campaign goal</option>
                {CAMPAIGN_GOALS.map(goal => (
                  <option key={goal} value={goal}>{goal}</option>
                ))}
              </select>
            </div>

            <div className="form-field form-width-short">
              <label>Creative Objective <span className="required">*</span></label>
              <select value={creativeObjective} onChange={(e) => setCreativeObjective(e.target.value)} required>
                <option value="">Select creative objective</option>
                {CREATIVE_OBJECTIVES.map(obj => (
                  <option key={obj} value={obj}>{obj}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Target Audience Section */}
          <div className="form-section">
            <h3 className="section-header">👥 Target Audience</h3>

            <div className="form-field">
              <label>Age Group <span className="required">*</span></label>
              <div className="checkbox-grid">
                {AGE_GROUPS.map(age => (
                  <label key={age} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={ageGroups.includes(age)}
                      onChange={() => toggleArrayValue(ageGroups, setAgeGroups, age)}
                    />
                    {age} Years
                  </label>
                ))}
              </div>
            </div>

            <div className="form-field form-width-short">
              <label>Gender <span className="required">*</span></label>
              <select value={gender} onChange={(e) => setGender(e.target.value)} required>
                {GENDERS.map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Target Location <span className="required">*</span></label>
              <input
                type="text"
                value={targetLocations}
                onChange={(e) => setTargetLocations(e.target.value)}
                placeholder="Mumbai, Delhi, Bangalore"
                required
              />
              <small className="field-hint">Separate multiple locations with commas</small>
            </div>

            <div className="form-field">
              <label>Languages <span className="required">*</span></label>
              <input
                type="text"
                value={languages}
                onChange={(e) => setLanguages(e.target.value)}
                placeholder="English, Hindi"
                required
              />
              <small className="field-hint">Separate multiple languages with commas</small>
            </div>

            <div className="form-field">
              <label>Audience Type <span className="required">*</span></label>
              <div className="checkbox-grid">
                {AUDIENCE_TYPES.map(type => (
                  <label key={type} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={audienceType.includes(type)}
                      onChange={() => toggleArrayValue(audienceType, setAudienceType, type)}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Offer & Creative Requirements Section */}
          <div className="form-section">
            <h3 className="section-header">💰 Offer & Creative Requirements</h3>

            <div className="form-field form-width-short">
              <label>Offer Type <span className="required">*</span></label>
              <select value={offerType} onChange={(e) => setOfferType(e.target.value)} required>
                <option value="">Select offer type</option>
                {OFFER_TYPES.map(offer => (
                  <option key={offer} value={offer}>{offer}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Offer Details</label>
              <input
                type="text"
                value={offerDetails}
                onChange={(e) => setOfferDetails(e.target.value)}
                placeholder="e.g., Flat 30% off on all eyewear"
              />
            </div>

            <div className="form-field">
              <label>Required Creative Formats <span className="required">*</span></label>
              <div className="checkbox-grid">
              {CREATIVE_FORMATS.map(format => (
                <label key={format.value} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={creativeFormats.includes(format.value)}
                    onChange={() => toggleArrayValue(creativeFormats, setCreativeFormats, format.value)}
                  />
                  {format.label}
                </label>
              ))}
              </div>
            </div>

            <div className="form-field form-width-short">
              <label>Number of Variants per Format <span className="required">*</span></label>
              <input
                type="number"
                min="1"
                max="5"
                value={numberOfVariants}
                onChange={(e) => setNumberOfVariants(parseInt(e.target.value))}
                required
              />
              <small className="field-hint">How many style variations to generate for each selected format</small>
            </div>

            <div className="form-field">
              <label>Additional Notes</label>
              <textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Any special instructions, do's and don'ts for the creatives..."
                rows="4"
              />
            </div>
          </div>

          <div className="button-group">
            <button
              type="button"
              className="secondary-button"
              onClick={() => setStep(1)}
            >
              ← Back
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={handleGenerateCreatives}
              disabled={loading || !keyCompetitors || !primaryCampaignGoal || ageGroups.length === 0 || !gender || !targetLocations || !languages || audienceType.length === 0 || !offerType || !creativeObjective || creativeFormats.length === 0}
            >
              {loading ? 'Generating Creatives...' : 'Generate Creatives 🎨'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && creatives.length > 0 && (
        <div className="results-container">
          {/* <h2 className="results-title">Generated Creatives</h2> */}
          <p className="results-subtitle">Your Meta-compliant ad creatives are ready!</p>

          
          <div className="creatives-grid">
            {creatives.map((creative) => (
              <div key={creative.variantNumber} className="creative-card">
                

                {creative.imageUrl && (
                  <div className="image-container">
                    <img
                      src={`${API_URL}${creative.imageUrl}`}
                      alt={creative.style}
                      loading="lazy"
                    />
                  </div>
                )}

                <div className="creative-header">
                  <h3>{creative.format}</h3>
                  <span className="variant-badge">{creative.style}</span>
                </div>

                {creative.description && (
                  <div className="description">
                    <p>{creative.description}</p>
                  </div>
                )}

                {creative.imageUrl && (
                  <div className="action-buttons">
                    <a
                      href={`${API_URL}${creative.imageUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="view-link"
                    >
                      View Full →
                    </a>
                    <button
                      onClick={() => handleDownload(
                        creative.imageUrl,
                        `${brandName}-${creative.format}-${creative.variantNumber}.png`
                      )}
                      className="download-link"
                    >
                      ⬇ Download
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="button-group">
            <button
              className="secondary-button"
              onClick={() => setStep(2)}
            >
              ← Back to Edit Details
            </button>
            <button
              className="secondary-button solid-bg"
              onClick={() => {
                setStep(1);
                setCreatives([]);
              }}
            >
              Create New Campaign
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreativeBriefForm;
